import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, RefreshCw, Copy, Check, Power, PowerOff, Trash2, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/grok-bots`;
const WEBHOOK = `${process.env.REACT_APP_BACKEND_URL}/api/grok/leads`;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export default function WingmanBotsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await axios.get(API);
      setRows(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const issue = async (u) => {
    if (u.key && !window.confirm(`Replace ${u.name}'s key? Their bot stops working until it gets the new key.`)) return;
    try {
      await axios.post(`${API}/${u.id}/key`);
      toast.success(`Key ready for ${u.name}. Click Copy on their row.`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create key');
    }
  };

  const toggle = async (u) => {
    await axios.patch(`${API}/${u.id}`, { active: !u.key.active });
    toast.success(u.key.active ? 'Key turned off' : 'Key turned on');
    refresh();
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}'s bot key?`)) return;
    await axios.delete(`${API}/${u.id}`);
    refresh();
  };

  return (
    <section className="space-y-5" data-testid="wingman-bots-section">
      <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 p-5">
        <h3 className="display text-[16px] font-bold text-neutral-900 dark:text-white tracking-tight inline-flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[hsl(var(--kf-gold-deep))]" /> Wingman bot keys
        </h3>
        <p className="text-[12.5px] text-neutral-600 dark:text-neutral-300 mt-2 max-w-[860px] leading-snug">
          Every bot posts to the same address. Each loan officer's bot uses its own key in the
          <code className="mono mx-1 px-1 rounded bg-neutral-100 dark:bg-white/[0.06] text-[11.5px]">X-Grok-Token</code>
          header, and every lead it sends is assigned to that LO in Dancing Shoes.
        </p>
        <code className="mt-3 block text-[12px] mono bg-neutral-50 dark:bg-neutral-900 border hairline rounded-md px-3 py-2 text-neutral-700 dark:text-neutral-300 break-all" data-testid="wingman-webhook-url">
          POST {WEBHOOK}
        </code>
      </div>

      {loading ? (
        <div className="text-[12px] text-neutral-500 mono animate-pulse">Loading team…</div>
      ) : (
        <div className="rounded-2xl border hairline bg-white dark:bg-neutral-950 divide-y divide-neutral-100 dark:divide-white/[0.05]" data-testid="wingman-bots-list">
          {rows.map((u) => (
            <BotRow key={u.id} u={u} onIssue={issue} onToggle={toggle} onDelete={remove} />
          ))}
        </div>
      )}
    </section>
  );
}

function CopyKey({ u }) {
  const [copied, setCopied] = useState(false);
  const token = u.key?.token;
  if (!token) return <span className="text-[11px] text-amber-700" data-testid={`wingman-bot-nocopy-${u.id}`}>Replace key to copy</span>;
  const copy = () => navigator.clipboard.writeText(token).then(
    () => { setCopied(true); setTimeout(() => setCopied(false), 1600); },
    () => toast.error('Copy blocked by browser'),
  );
  return (
    <button
      onClick={copy}
      data-testid={`wingman-bot-copy-${u.id}`}
      className={cn(
        'h-8 px-3 rounded-md text-[11.5px] font-bold inline-flex items-center gap-1.5 transition-colors',
        copied ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-neutral-900 text-[hsl(var(--kf-gold))] hover:bg-neutral-800',
      )}
    >
      {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy key</>}
    </button>
  );
}

function BotRow({ u, onIssue, onToggle, onDelete }) {
  const k = u.key;
  const btn = 'h-8 px-3 rounded-md text-[11.5px] font-bold inline-flex items-center gap-1.5 border hairline hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-colors';
  return (
    <div className={cn('px-5 py-3.5 flex items-center gap-4 flex-wrap', k && !k.active && 'opacity-60')} data-testid={`wingman-bot-row-${u.id}`}>
      <div className="min-w-[200px] flex-1">
        <div className="text-[13.5px] font-semibold text-neutral-900 dark:text-white">{u.name}</div>
        <div className="text-[11.5px] text-neutral-500">{u.email}</div>
      </div>
      <div className="text-[11.5px] text-neutral-500 min-w-[260px]" data-testid={`wingman-bot-status-${u.id}`}>
        {k ? (
          <>
            <span className="mono text-neutral-800 dark:text-neutral-200">{k.prefix}…</span>
            <span className="mx-2 text-neutral-300">·</span>{k.active ? 'On' : 'Off'}
            <span className="mx-2 text-neutral-300">·</span>{k.total_received} {k.total_received === 1 ? 'lead' : 'leads'}
            <span className="mx-2 text-neutral-300">·</span>Last used {fmt(k.last_used_at)}
          </>
        ) : 'No key yet'}
      </div>
      <div className="flex items-center gap-2">
        {k && <CopyKey u={u} />}
        {k && (
          <button className={btn} onClick={() => onToggle(u)} data-testid={`wingman-bot-toggle-${u.id}`}>
            {k.active ? <PowerOff className="h-3.5 w-3.5 text-rose-600" /> : <Power className="h-3.5 w-3.5 text-emerald-600" />}
            {k.active ? 'Turn off' : 'Turn on'}
          </button>
        )}
        <button className={btn} onClick={() => onIssue(u)} data-testid={`wingman-bot-issue-${u.id}`}>
          {k ? <RefreshCw className="h-3.5 w-3.5" /> : <KeyRound className="h-3.5 w-3.5" />}
          {k ? 'Replace key' : 'Create key'}
        </button>
        {k && (
          <button className={cn(btn, 'border-rose-200 text-rose-600 hover:bg-rose-50')} onClick={() => onDelete(u)} data-testid={`wingman-bot-delete-${u.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
